// [PURPOSE]: Replayable Help / Tour menu — lists every available tour with
//   per-module "Replay" / "Start" buttons so users can re-watch individual tours
//   without wiping all tutorial progress (TUTORIAL-1).
// [WHY]: The only existing replay path (Settings → "Reset All Tours") is all-or-nothing.
//   A user who wants to re-watch just the spellbook tour had no way to do so. This
//   modal provides per-module, user-initiated replay at any time.
// [DESIGN]: findings doc 2026-06-20-tutorial-onboarding-design-tod.md §3 Phase 1 (A).
// [INTEGRATION]:
//   - useTutorial() from TutorialContext (replayModule, isModuleComplete, completedModules)
//   - TUTORIAL_MODULES + PREMIUM_TUTORIAL_MODULES from tutorialModules.js
//   - getReplayableModules() from utils/tutorialUtils.js (shared with tests)
//   - MobileModal from ../MobileModal (self-registers useModalEscape, Rule 19 —
//     UI-MOBILEMODAL-ADOPTION migration)
//   - usePremiumAccess from hooks/usePremiumAccess (mirrors WelcomePopup gating)

import React from 'react';
import { Play, CheckCircle, Lock, BookOpen } from 'lucide-react';
import MobileModal from '../MobileModal';
import { useTutorial } from '../../contexts/TutorialContext';
import { usePremiumAccess } from '../../hooks/usePremiumAccess';
import { TUTORIAL_MODULES, PREMIUM_TUTORIAL_MODULES } from '../../data/tutorialModules';
import { getReplayableModules } from '../../utils/tutorialUtils';

// ========================================
// TUTORIAL HELP MENU COMPONENT
// ========================================

/**
 * Modal that lists every replayable tutorial module with:
 * - Module title + one-line description (from promptMessage or a generic fallback)
 * - Completion checkmark when isModuleComplete(id) is true
 * - "Replay" (if completed) or "Start" (if not) button → replayModule(id) then onClose
 * - Premium modules: shown but disabled/locked when !hasPremium (mirrors WelcomePopup)
 *
 * @param {boolean}  isOpen  — controls visibility
 * @param {function} onClose — dismiss callback
 */
export default function TutorialHelpMenu({ isOpen, onClose }) {
  const { replayModule, isModuleComplete } = useTutorial();
  const hasPremium = usePremiumAccess();

  // [UI-MOBILEMODAL-ADOPTION wave 2a]: short-circuit BEFORE building the module list —
  // the .map below calls isModuleComplete() during THIS component's render, which throws
  // when the menu is mounted closed outside a TutorialProvider (e.g. SettingsPanel render
  // tests). MobileModal also gates on isOpen, but our body computes before reaching it.
  // The hooks above run unconditionally, so this guard is rules-of-hooks safe.
  if (!isOpen) return null;

  const replayableModules = getReplayableModules(TUTORIAL_MODULES);

  const handleReplay = (moduleId) => {
    replayModule(moduleId);
    onClose();
  };

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <BookOpen size={20} className="text-purple-400" />
          Browse Tours
        </span>
      }
      showSaveButton={false}
      overlayClassName="z-[200]"
    >
      {/* Subtitle */}
      <p className="text-gray-400 text-xs mb-3">
        Re-watch any individual guided tour. Your progress on other tours is not affected.
      </p>

      {/* Module list */}
      <ul className="space-y-1.5">
        {replayableModules.map((mod) => {
          const isPremium = PREMIUM_TUTORIAL_MODULES.has(mod.id);
          const isLocked = isPremium && !hasPremium;
          const isComplete = isModuleComplete(mod.id);

          // Derive one-line description: prefer promptMessage, fall back to step count
          const description = mod.promptMessage
            ? mod.promptMessage
            : `${mod.steps.length} step${mod.steps.length !== 1 ? 's' : ''}`;

          return (
            <li key={mod.id}>
              <div
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${
                  isLocked
                    ? 'bg-gray-800/40 opacity-60'
                    : 'bg-gray-800/60 hover:bg-gray-800'
                }`}
              >
                {/* Completion indicator */}
                <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                  {isLocked ? (
                    <Lock size={14} className="text-gray-500" />
                  ) : isComplete ? (
                    <CheckCircle size={16} className="text-green-400" />
                  ) : (
                    <div className="w-3 h-3 rounded-full border-2 border-gray-600" />
                  )}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-white text-sm font-medium">{mod.title}</span>
                    {isPremium && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-700/60 text-amber-300 rounded font-medium">
                        Premium
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{description}</p>
                </div>

                {/* Action button */}
                {isLocked ? (
                  <span className="flex-shrink-0 text-xs text-gray-500 font-medium">
                    Upgrade
                  </span>
                ) : (
                  <button
                    onClick={() => handleReplay(mod.id)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-purple-700 hover:bg-purple-600 active:bg-purple-800 text-white text-xs font-medium rounded-lg transition-colors min-w-[56px] justify-center"
                    aria-label={`${isComplete ? 'Replay' : 'Start'} ${mod.title} tour`}
                    style={{ minHeight: '36px' }}
                  >
                    <Play size={11} />
                    {isComplete ? 'Replay' : 'Start'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Footer caption */}
      <p className="text-xs text-gray-500 text-center mt-4 pt-3 border-t border-gray-700/50">
        Replaying a tour clears only that tour's completion — other progress is kept.
      </p>
    </MobileModal>
  );
}
